'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Icon } from '@saas/ui';

const TIMEZONES = [
  'Asia/Kuala_Lumpur',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Australia/Sydney',
];

const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'ms-MY', label: 'Bahasa Malaysia' },
  { value: 'zh-CN', label: '中文 (简体)' },
  { value: 'zh-TW', label: '中文 (繁體)' },
];

export function UserPanel({ isOpen, onClose, user }: any) {
  const router = useRouter();
  const [userData, setUserData] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeSection, setActiveSection] = useState<'basic' | 'identity' | 'contact' | 'address' | 'work'>('basic');

  useEffect(() => {
    if (isOpen && !userData) {
      setIsFetching(true);
      fetch('/api/user/profile')
        .then((res) => res.json())
        .then((data) => {
          setUserData(data);
          setIsFetching(false);
        })
        .catch((err) => {
          console.error('Fetch error:', err);
          setMessage({ type: 'error', text: 'Failed to load profile' });
          setIsFetching(false);
        });
    }
  }, [isOpen, userData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const res = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update profile');

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      router.refresh();
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  const sections = [
    { id: 'basic', label: 'Basic', icon: 'User' },
    { id: 'identity', label: 'Identity', icon: 'Fingerprint' },
    { id: 'contact', label: 'Contact', icon: 'Phone' },
    { id: 'address', label: 'Address', icon: 'MapPin' },
    { id: 'work', label: 'Work', icon: 'Briefcase' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center gap-3">
            {userData?.image ? (
              <img src={userData.image} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white bg-blue-600">
                {(userData?.name || userData?.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Profile Settings</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{userData?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none">
            &times;
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id as any)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeSection === section.id
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <Icon name={section.icon} size={16} />
              <span className="hidden sm:inline">{section.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {message && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-200'
              }`}
            >
              <Icon name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
              {message.text}
            </div>
          )}

          {isFetching ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-6">
              {/* Basic Information */}
              {activeSection === 'basic' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Full Name *</label>
                      <input name="name" type="text" value={userData?.name || ''} onChange={handleChange} className={inputClass} required />
                    </div>
                    <div>
                      <label className={labelClass}>Username</label>
                      <input name="username" type="text" value={userData?.username || ''} onChange={handleChange} className={inputClass} placeholder="Display name" />
                    </div>
                    <div>
                      <label className={labelClass}>Email (read-only)</label>
                      <input type="email" value={userData?.email || ''} disabled className={inputClass + ' opacity-60 cursor-not-allowed'} />
                    </div>
                    <div>
                      <label className={labelClass}>Mobile Phone</label>
                      <input name="phone" type="tel" value={userData?.phone || ''} onChange={handleChange} className={inputClass} placeholder="+60 12-345 6789" />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Bio</label>
                    <textarea name="bio" value={userData?.bio || ''} onChange={handleChange} rows={3} className={inputClass + ' resize-none'} placeholder="Tell us about yourself..." />
                  </div>
                </div>
              )}

              {/* Identity & Personal Details */}
              {activeSection === 'identity' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Date of Birth</label>
                      <input name="dateOfBirth" type="date" value={userData?.dateOfBirth ? new Date(userData.dateOfBirth).toISOString().split('T')[0] : ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Gender</label>
                      <select name="gender" value={userData?.gender || ''} onChange={handleChange} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="OTHER">Other</option>
                        <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Marital Status</label>
                      <select name="maritalStatus" value={userData?.maritalStatus || ''} onChange={handleChange} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="SINGLE">Single</option>
                        <option value="MARRIED">Married</option>
                        <option value="DIVORCED">Divorced</option>
                        <option value="WIDOWED">Widowed</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Nationality</label>
                      <input name="nationality" type="text" value={userData?.nationality || ''} onChange={handleChange} className={inputClass} placeholder="e.g., Malaysian" />
                    </div>
                    <div>
                      <label className={labelClass}>National ID / SSN / Tax ID</label>
                      <input name="nationalId" type="text" value={userData?.nationalId || ''} onChange={handleChange} className={inputClass + ' font-mono'} placeholder="Sensitive information" />
                    </div>
                    <div>
                      <label className={labelClass}>Passport Number</label>
                      <input name="passportNumber" type="text" value={userData?.passportNumber || ''} onChange={handleChange} className={inputClass + ' font-mono'} />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Driver's License Number</label>
                      <input name="driversLicense" type="text" value={userData?.driversLicense || ''} onChange={handleChange} className={inputClass + ' font-mono'} />
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Contact */}
              {activeSection === 'contact' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Alternate Email</label>
                      <input name="alternateEmail" type="email" value={userData?.alternateEmail || ''} onChange={handleChange} className={inputClass} placeholder="backup@example.com" />
                    </div>
                    <div>
                      <label className={labelClass}>Alternate Phone / Landline</label>
                      <input name="alternatePhone" type="tel" value={userData?.alternatePhone || ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Preferred Contact Method</label>
                      <select name="preferredContact" value={userData?.preferredContact || ''} onChange={handleChange} className={inputClass}>
                        <option value="">Select...</option>
                        <option value="EMAIL">Email</option>
                        <option value="PHONE">Phone</option>
                        <option value="SMS">SMS</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Address */}
              {activeSection === 'address' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className={labelClass}>Street Address Line 1</label>
                      <input name="addressLine1" type="text" value={userData?.addressLine1 || ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Street Address Line 2 (Apt, Suite, Unit)</label>
                      <input name="addressLine2" type="text" value={userData?.addressLine2 || ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>City / Town</label>
                      <input name="city" type="text" value={userData?.city || ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>State / Province / Region</label>
                      <input name="state" type="text" value={userData?.state || ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Postal / ZIP Code</label>
                      <input name="postalCode" type="text" value={userData?.postalCode || ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Country</label>
                      <input name="country" type="text" value={userData?.country || ''} onChange={handleChange} className={inputClass} />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>County / District</label>
                      <input name="county" type="text" value={userData?.county || ''} onChange={handleChange} className={inputClass} />
                    </div>
                  </div>
                </div>
              )}

              {/* Work Details */}
              {activeSection === 'work' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Job Title</label>
                      <input name="jobTitle" type="text" value={userData?.jobTitle || ''} onChange={handleChange} className={inputClass} placeholder="e.g., CTO, Finance Manager" />
                    </div>
                    <div>
                      <label className={labelClass}>Department</label>
                      <input name="department" type="text" value={userData?.department || ''} onChange={handleChange} className={inputClass} placeholder="e.g., Engineering, Sales" />
                    </div>
                    <div>
                      <label className={labelClass}>Timezone</label>
                      <select name="timezone" value={userData?.timezone || 'Asia/Kuala_Lumpur'} onChange={handleChange} className={inputClass}>
                        {TIMEZONES.map((tz) => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Language</label>
                      <select name="locale" value={userData?.locale || 'en-US'} onChange={handleChange} className={inputClass}>
                        {LOCALES.map((loc) => (
                          <option key={loc.value} value={loc.value}>{loc.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
                  Cancel
                </button>
                <button type="submit" disabled={isSaving} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center gap-2">
                  {isSaving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
